/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import useSWR from 'swr';
import qs from 'qs';

import request from '@/utils/request';
import type * as Type from '@/common/interface';

export const usePublicDownloadRecommendations = (
  params: Type.PublicDownloadRecommendationReq,
) => {
  const apiUrl = `/answer/api/v1/download-recommendations?${qs.stringify(
    params,
    { skipNulls: true },
  )}`;
  const { data, error, mutate } = useSWR<
    Type.ListResult<Type.PublicDownloadRecommendationItem>,
    Error
  >([apiUrl], request.instance.get);
  return {
    data,
    isLoading: !data && !error,
    error,
    mutate,
  };
};

export const usePublicDownloadRecommendationDetail = (id?: string | number) => {
  const apiUrl = id
    ? `/answer/api/v1/download-recommendations/detail?id=${encodeURIComponent(
        String(id),
      )}`
    : null;
  const { data, error, mutate } = useSWR<
    Type.PublicDownloadRecommendationDetail,
    Error
  >(apiUrl ? [apiUrl] : null, request.instance.get);
  return {
    data,
    isLoading: Boolean(apiUrl) && !data && !error,
    error,
    mutate,
  };
};

export const createDownloadRecommendationTicket = (
  id: number,
  sourcePage: 'downloads' | 'official_site' = 'downloads',
) => {
  return request.post<Type.DownloadRecommendationTicketResp>(
    '/answer/api/v1/download-recommendations/ticket',
    { id, source_page: sourcePage },
  );
};
